import { ActivityPointRankMyDto } from '@diritto/common/activityPoint/dtos/activity-point.dtos';
import { ProfileLiteRelayDto } from '@diritto/common/relay/dtos/profile-lite-relay.dtos';
import { Color } from '@diritto/common/style/color';
import { FontWeight, Typography } from '@diritto/common/style/typography';
import { numberComma } from '@diritto/common/utils/string';
import { generateUrl, parseUrl } from '@diritto/common/utils/urls';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { ProfileImage } from 'app-components/accounts/profiles/ProfileImage';
import { ContentBodyPadding } from 'constants/layout';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import React, { useEffect, useRef } from 'react';
import { getTextLineStyle } from 'styles/common';
import { Link } from 'ui-components/common/Link';
import { useIsInViewport } from 'ui-hooks/useIsInViewport';

export enum ProfileRankingStatus {
  InnerRankLimit = 'InnerRankLimit',
  InnerRankLimitMyProfile = 'InnerRankLimitMyProfile',
  OuterRankLimit = 'OuterRankLimit',
}

export const ProfileRanking = ({
  activityPointMyRank,
  status,
  profileLite,
  isFocused = false,
}: {
  activityPointMyRank: ActivityPointRankMyDto;
  status: ProfileRankingStatus;
  profileLite: ProfileLiteRelayDto;
  isFocused?: boolean;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const isInViewport = useIsInViewport(ref, isFocused);

  useEffect(() => {
    if (isFocused && ref?.current) {
      const interval = setInterval(() => {
        ref?.current?.scrollIntoView({ behavior: 'auto', block: 'center', inline: 'center' });
      }, 50);

      return () => clearInterval(interval);
    }
    return () => {};
  }, [isFocused, router]);

  useEffect(() => {
    if (isFocused && isInViewport) {
      const { path, queryParams } = parseUrl(`${pathname}?${searchParams?.toString()}`);
      const url = generateUrl(path, { ...queryParams, focusMyRank: undefined });
      window.history.replaceState({}, '', url);
    }
  }, [isFocused, isInViewport, pathname, router, searchParams]);

  if (profileLite?.isDeleted) {
    return null;
  }

  return (
    <StyledProfileRankingWrapper ref={ref} status={status}>
      <StyledProfileRankingProfileImage>
        <Link href={`/accounts/profiles/${profileLite.id}`}>
          <ProfileImage file={profileLite?.profileImage} defaultImageKey={profileLite?.nickname} size={32} />
        </Link>
      </StyledProfileRankingProfileImage>
      <StyledProfileRankingRank>{activityPointMyRank.rank ?? '-'}</StyledProfileRankingRank>

      <StyledProfileRankingProfileName>
        <Link href={`/accounts/profiles/${profileLite.id}`}>{profileLite.nickname}</Link>
      </StyledProfileRankingProfileName>
      <StyledProfileRankingPoint>{numberComma(activityPointMyRank.point)}점</StyledProfileRankingPoint>
      <StyledDivider />
    </StyledProfileRankingWrapper>
  );
};

const getBackgroundColor = (status: ProfileRankingStatus) =>
  ({
    [ProfileRankingStatus.InnerRankLimitMyProfile]: Color.Primary100,
    [ProfileRankingStatus.InnerRankLimit]: Color.White,
    [ProfileRankingStatus.OuterRankLimit]: Color.Gray100,
  })[status];

const StyledProfileRankingWrapper = styled.div(
  ({ status }: { status: ProfileRankingStatus }) => css`
    position: relative;
    padding: 0 ${ContentBodyPadding}px;
    height: 78px;

    display: flex;
    flex-direction: row;
    align-items: center;
    background: ${getBackgroundColor(status)};
  `,
);

const StyledProfileRankingProfileImage = styled.div`
  width: 32px;
  margin-right: 8px;
`;

const StyledProfileRankingRank = styled.div`
  width: 48px;
  display: flex;
  justify-content: center;
  margin-right: 8px;

  color: ${Color.Black};
  font-weight: ${FontWeight.Medium};
  ${Typography.XXSmall};
`;

const StyledProfileRankingProfileName = styled.div`
  flex-grow: 1;
  margin-right: 8px;

  color: ${Color.Black};
  font-weight: ${FontWeight.Medium};
  ${Typography.XXSmall};

  ${getTextLineStyle(1)};
`;

const StyledProfileRankingPoint = styled.div`
  color: ${Color.Gray600};
  font-weight: ${FontWeight.Medium};
  ${Typography.XXSmall};
`;

const StyledDivider = styled.div`
  display: flex;
  position: absolute;
  width: calc(100% - ${ContentBodyPadding * 2}px);
  bottom: 0;
  height: 1px;
  background: ${Color.Gray200};
`;
